/** Stockage local isolé par utilisateur et espace ; aucun contenu partagé entre onglets. */
import { useCallback, useState, useSyncExternalStore } from 'react';

export function readLocal<T>(key: string, fallback: T): T {
  try {
    const text = localStorage.getItem(`ndrc:v1:${key}`);
    if (text === null) return fallback;
    const parsed: unknown = JSON.parse(text);
    if (fallback !== null && typeof fallback === 'object') {
      if (
        parsed === null ||
        typeof parsed !== 'object' ||
        Array.isArray(parsed) !== Array.isArray(fallback)
      )
        return fallback;
      for (const [field, expected] of Object.entries(fallback)) {
        if (
          expected !== null &&
          typeof (parsed as Record<string, unknown>)[field] !== typeof expected
        )
          return fallback;
      }
    } else if (fallback !== null && typeof parsed !== typeof fallback) return fallback;
    return parsed as T;
  } catch {
    return fallback;
  }
}
export function writeLocal(key: string, value: unknown): boolean {
  try {
    if (value === undefined) localStorage.removeItem(`ndrc:v1:${key}`);
    else localStorage.setItem(`ndrc:v1:${key}`, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}
export function useLocalState<T>(key: string, initial: T) {
  const [value, setValue] = useState<T>(() => readLocal(key, initial));
  const [stored, setStored] = useState(true);
  const set = useCallback(
    (next: T) => {
      setValue(next);
      setStored(writeLocal(key, next));
    },
    [key],
  );
  return [value, set, stored] as const;
}
const CHANGE = 'ndrc:data-changed';
export function notifyDataChanged(source: string): void {
  window.dispatchEvent(new CustomEvent(CHANGE, { detail: source }));
  // Un signal sans donnée personnelle : les destinataires relisent sous leur propre session.
  writeLocal(CHANGE, { source, nonce: crypto.randomUUID() });
}
export function subscribeDataChanges(source: string, refresh: () => void): () => void {
  const local = (event: Event) => {
    if ((event as CustomEvent<string>).detail !== source) refresh();
  };
  const remote = (event: StorageEvent) => {
    if (event.key === `ndrc:v1:${CHANGE}`) refresh();
  };
  window.addEventListener(CHANGE, local);
  window.addEventListener('storage', remote);
  window.addEventListener('online', refresh);
  // Inclut les modifications faites depuis un autre navigateur/session.
  const timer = window.setInterval(refresh, 10000);
  return () => {
    window.removeEventListener(CHANGE, local);
    window.removeEventListener('storage', remote);
    window.removeEventListener('online', refresh);
    window.clearInterval(timer);
  };
}
const listenOnline = (listener: () => void) => {
  window.addEventListener('online', listener);
  window.addEventListener('offline', listener);
  return () => {
    window.removeEventListener('online', listener);
    window.removeEventListener('offline', listener);
  };
};
export function useOnline(): boolean {
  return useSyncExternalStore(
    listenOnline,
    () => navigator.onLine,
    () => true,
  );
}
